const BUILD_ID_KEY = 'APP_BUILD_ID';
const RELOAD_GUARD_KEY = 'app_update_reload_guard';
const MAIN_SCRIPT_RELOAD_KEY = 'app_main_script_reload';
const MAX_MAIN_SCRIPT_RELOADS = 1;
const INITIAL_CHECK_DELAY_MS = 5000;
const UPDATE_POLL_INTERVAL_MS = 5 * 60 * 1000;

function getLoadedMainBuildId(): string | null {
  const script = document.querySelector('script[src*="/static/js/main."]');
  const src = script?.getAttribute('src') || '';
  const match = src.match(/\/main\.([a-f0-9]+)\.js/);
  return match?.[1] ?? null;
}

async function clearRuntimeCaches(): Promise<void> {
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
}

function markBuildAsCurrent(buildId: string): void {
  localStorage.setItem(BUILD_ID_KEY, buildId);
  sessionStorage.removeItem(MAIN_SCRIPT_RELOAD_KEY);
}

async function reloadOnceForBuild(buildId: string): Promise<void> {
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === buildId) {
    // Already reloaded once for this build — accept it to break infinite loops.
    markBuildAsCurrent(buildId);
    return;
  }

  localStorage.setItem(BUILD_ID_KEY, buildId);
  sessionStorage.setItem(RELOAD_GUARD_KEY, buildId);
  await clearRuntimeCaches();
  window.location.reload();
}

export function initMainScriptRecovery(): void {
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLScriptElement)) {
        return;
      }

      const scriptSrc = target.src || '';
      if (!scriptSrc.includes('/static/js/main.')) {
        return;
      }

      const attempts = Number(sessionStorage.getItem(MAIN_SCRIPT_RELOAD_KEY) || '0');
      if (attempts >= MAX_MAIN_SCRIPT_RELOADS) {
        return;
      }

      sessionStorage.setItem(MAIN_SCRIPT_RELOAD_KEY, String(attempts + 1));
      void clearRuntimeCaches().then(() => {
        window.location.reload();
      });
    },
    true
  );
}

export function initDeploymentVersionCheck(): void {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  let checking = false;

  const checkForUpdate = async () => {
    if (checking) {
      return;
    }

    checking = true;
    try {
      const response = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        return;
      }

      const payload = await response.json();
      const serverBuildId = payload?.buildId as string | undefined;
      if (!serverBuildId) {
        return;
      }

      const loadedBuildId = getLoadedMainBuildId();

      // App is running the latest deployed bundle — stay on the page.
      if (loadedBuildId && loadedBuildId === serverBuildId) {
        markBuildAsCurrent(serverBuildId);
        return;
      }

      // Server has a newer build than what is loaded — reload once.
      console.info('New deployment detected. Refreshing once...');
      await reloadOnceForBuild(serverBuildId);
    } catch (error) {
      console.debug('Deployment version check skipped:', error);
    } finally {
      checking = false;
    }
  };

  window.setTimeout(() => {
    void checkForUpdate();
  }, INITIAL_CHECK_DELAY_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate();
    }
  });

  window.addEventListener('focus', () => {
    void checkForUpdate();
  });

  window.setInterval(() => {
    void checkForUpdate();
  }, UPDATE_POLL_INTERVAL_MS);
}
